import { motion } from "framer-motion";
import { Check, Download, Flame, Loader2, PhoneCall, X } from "lucide-react";

export const STATUS_STYLES = {
  qualified: "border-amber-500/40 text-amber-400 bg-amber-500/10",
  submitted: "border-cyan-500/40 text-cyan-400 bg-cyan-500/10",
  approved: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  declined: "border-rose-500/40 text-rose-400 bg-rose-500/10",
};

const smsLabel = (alert) => (alert?.status === "sent" ? "sent" : alert?.status === "failed" ? "failed" : "pending");

export default function LeadCard({ lead, index, statusBusy, onStatus, onDownload, hot = false }) {
  const short = lead.id.slice(0, 8);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
      className={`glass-panel rounded-md p-6 ${hot ? "border border-orange-500/30 shadow-[0_0_40px_-20px_rgba(249,115,22,0.6)]" : ""}`}
      data-testid={`admin-lead-${short}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`text-xs font-mono-d tracking-[0.15em] uppercase px-2.5 py-1 rounded-sm border ${STATUS_STYLES[lead.status] || STATUS_STYLES.qualified}`}
            data-testid={`lead-status-${short}`}
          >
            {lead.status}
          </span>
          <span className="text-xs font-mono-d text-zinc-500">{short.toUpperCase()}</span>
          {lead.hot_file && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-mono-d tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-orange-500/40 text-orange-400 bg-orange-500/10"
              data-testid={`hot-file-${short}`}
            >
              <Flame className="w-3 h-3" /> Hot file
              <span className="text-orange-400/60">· SMS {smsLabel(lead.sms_alert)}</span>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-zinc-500">{new Date(lead.created_at).toLocaleString()}</span>
          {lead.contact_phone ? (
            <a
              href={`tel:${lead.contact_phone}`}
              data-testid={`call-now-${short}`}
              className="inline-flex items-center gap-1.5 text-xs font-mono-d tracking-[0.1em] uppercase bg-orange-500 hover:bg-orange-400 text-[#07090D] font-semibold px-3 py-1.5 rounded-sm transition-colors"
            >
              <PhoneCall className="w-3.5 h-3.5" /> Call now
            </a>
          ) : hot ? (
            <span className="text-[10px] font-mono-d tracking-[0.1em] uppercase text-zinc-600" data-testid={`no-phone-${short}`}>
              No callback # yet
            </span>
          ) : null}
          {lead.status !== "approved" && lead.status !== "declined" && (
            <div className="flex gap-2">
              <button
                onClick={() => onStatus(lead.id, "approved")}
                disabled={statusBusy !== null}
                data-testid={`approve-lead-${short}`}
                className="inline-flex items-center gap-1.5 text-xs font-mono-d tracking-[0.1em] uppercase border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              >
                {statusBusy === lead.id + "approved" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Approve
              </button>
              <button
                onClick={() => onStatus(lead.id, "declined")}
                disabled={statusBusy !== null}
                data-testid={`decline-lead-${short}`}
                className="inline-flex items-center gap-1.5 text-xs font-mono-d tracking-[0.1em] uppercase border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-40"
              >
                {statusBusy === lead.id + "declined" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                Decline
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
        <div>
          <p className="text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 mb-1">Revenue</p>
          <p className={hot ? "text-orange-300 font-medium" : "text-zinc-200"}>${Number(lead.monthly_revenue).toLocaleString()} / mo</p>
        </div>
        <div>
          <p className="text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 mb-1">Credit score</p>
          <p className="text-zinc-200">{lead.credit_score}</p>
        </div>
        <div>
          <p className="text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 mb-1">Liens / BK (5y)</p>
          <p className={lead.liens_or_bankruptcies ? "text-rose-400" : "text-zinc-200"}>{lead.liens_or_bankruptcies ? "Yes" : "No"}</p>
        </div>
        <div>
          <p className="text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 mb-1">Positions</p>
          <p className="text-zinc-200">{lead.active_positions}</p>
        </div>
        <div>
          <p className="text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 mb-1">Prior default</p>
          <p className={lead.defaulted ? "text-amber-400" : "text-zinc-200"}>
            {lead.defaulted ? `Yes · ZBL ${lead.zbl_ready ? "ready" : "missing"}` : "No"}
          </p>
        </div>
      </div>
      {(lead.contact_phone || lead.signer_name) && (
        <p className="mt-4 text-xs text-zinc-400 flex flex-wrap items-center gap-x-4 gap-y-1">
          {lead.contact_phone && (
            <span data-testid={`lead-phone-${short}`}>
              Callback {lead.contact_name ? <span className="text-zinc-200">{lead.contact_name}</span> : null}{" "}
              <span className="font-mono-d text-orange-300">{lead.contact_phone}</span>
            </span>
          )}
          {lead.signer_name && (
            <span>
              Signed by <span className="font-script text-xl text-amber-400 ml-1">{lead.signer_name}</span>
              {lead.signed_at && <span className="ml-2 text-zinc-500">· {new Date(lead.signed_at).toLocaleString()}</span>}
            </span>
          )}
        </p>
      )}
      {lead.documents?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {lead.documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => onDownload(doc)}
              data-testid={`download-doc-${doc.id.slice(0, 8)}`}
              className="inline-flex items-center gap-2 text-xs border border-white/15 hover:border-amber-500/40 text-zinc-300 px-3 py-1.5 rounded-sm transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {doc.kind === "signature" ? "e-signature.png" : doc.original_filename}
              <span className="text-zinc-600">{(doc.size / 1024).toFixed(0)} KB</span>
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}
